const DRAFTS_KEY = "cls_lacw_local_test_drafts_v1";
const WORKSPACE_KEY = "cls_lacw_local_test_workspace_v1";
const EXPORT_VERSION = 1;

export const LACW_LOCAL_TEST_EVENT = "cls-lacw-local-test-data-changed";

const PRODUCTION_PORTAL_HOSTS = new Set([
  "portal.completelawsupport.com",
  "emma-frontend-blush.vercel.app",
  "emma-frontend-ejackson-8186s-projects.vercel.app",
  "emma-frontend-ejackson-8186-ejackson-8186s-projects.vercel.app",
]);

export function isLacwLocalTestMode({
  dev = Boolean(import.meta.env?.DEV),
  flag = import.meta.env?.VITE_LACW_LOCAL_TEST_MODE,
  previewFlag = import.meta.env?.VITE_LACW_LOCAL_TEST_PREVIEW,
  hostname = typeof window !== "undefined" ? window.location?.hostname : "",
} = {}) {
  if (dev) return String(flag || "").toLowerCase() === "true";
  const host = String(hostname || "").toLowerCase();
  return String(previewFlag || "").toLowerCase() === "true"
    && host.endsWith(".vercel.app")
    && !PRODUCTION_PORTAL_HOSTS.has(host);
}

function browserStorage() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage; } catch { return null; }
}

function readJson(key, fallback) {
  const storage = browserStorage();
  if (!storage) return fallback;
  try { return JSON.parse(storage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
}

function writeJson(key, value) {
  const storage = browserStorage();
  if (!storage) throw new Error("Local test storage is unavailable in this browser");
  storage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(LACW_LOCAL_TEST_EVENT));
}

const timestamp = () => new Date().toISOString();

function seedWorkspace() {
  const created = timestamp();
  return {
    matters: [
      { id: "LACW-TEST-1001", client_name: "Mia Sample", client_email: "mia.sample@example.test", client_phone: "0400 000 101", status: "Open", notes: "Fictional criminal law matter for local portal testing only.", created_at: created, updated_at: created },
      { id: "LACW-TEST-1002", client_name: "Nora Example", client_email: "nora.example@example.test", client_phone: "0400 000 102", status: "Open", notes: "Fictional family violence matter for local portal testing only.", created_at: created, updated_at: created },
    ],
    tasks: [
      { id: "task_lacw_test_1", matter_id: "LACW-TEST-1001", task_title: "Review fictional intake", status: "Pending" },
      { id: "task_lacw_test_2", matter_id: "LACW-TEST-1002", task_title: "Prepare sample follow-up", status: "Pending" },
    ],
    documents: [],
    timeline: [
      { id: "activity_lacw_test_1", matter_id: "LACW-TEST-1001", timestamp: created, action: "Loaded fictional local test matter", details: "Loaded fictional local test matter" },
      { id: "activity_lacw_test_2", matter_id: "LACW-TEST-1002", timestamp: created, action: "Loaded fictional local test matter", details: "Loaded fictional local test matter" },
    ],
  };
}

function seedDrafts() {
  const created = timestamp();
  return [{
    id: "draft_lacw_test_intake_1", form_type: "intake_form", draft_name: "Mia Sample - fictional intake",
    saved_by: "Local test user", saved_by_email: "tester@example.test", brand: "lacw", matter_id: "LACW-TEST-1001",
    form_data: { first_name: "Mia", last_name: "Sample", email: "mia.sample@example.test", matter_number: "LACW-TEST-1001", test_data: true },
    created_at: created, updated_at: created,
  }];
}

function ensureSeeded() {
  if (!isLacwLocalTestMode()) return;
  const storage = browserStorage();
  if (!storage) return;
  if (!storage.getItem(WORKSPACE_KEY)) writeJson(WORKSPACE_KEY, seedWorkspace());
  if (!storage.getItem(DRAFTS_KEY)) writeJson(DRAFTS_KEY, seedDrafts());
}

function validateImport(payload) {
  if (!payload || typeof payload !== "object" || payload.version !== EXPORT_VERSION) throw new Error("This is not a supported LACW local test data file");
  if (!Array.isArray(payload.drafts) || !payload.workspace || typeof payload.workspace !== "object") throw new Error("The LACW local test data file is incomplete");
  if (payload.drafts.some((draft) => draft?.brand !== "lacw")) throw new Error("The import contains records outside the LACW test workspace");
}

export const lacwLocalTestStore = {
  listDrafts(formType) {
    ensureSeeded();
    return readJson(DRAFTS_KEY, []).filter((draft) => !formType || draft.form_type === formType).sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
  },
  getDraft(id) { ensureSeeded(); return readJson(DRAFTS_KEY, []).find((draft) => draft.id === id) || null; },
  saveDraft({ id, formType, draftName, savedBy, savedByEmail, formData, matter_id }) {
    ensureSeeded();
    const drafts = readJson(DRAFTS_KEY, []);
    const draftId = id || `lacw_test_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const existing = drafts.find((draft) => draft.id === draftId);
    const updated = timestamp();
    const saved = { id: draftId, form_type: formType, draft_name: draftName, saved_by: savedBy || "Local test user", saved_by_email: savedByEmail || "tester@example.test", brand: "lacw", form_data: formData, matter_id: matter_id || null, created_at: existing?.created_at || updated, updated_at: updated };
    writeJson(DRAFTS_KEY, [saved, ...drafts.filter((draft) => draft.id !== draftId)]);
    return saved;
  },
  deleteDraft(id) { ensureSeeded(); writeJson(DRAFTS_KEY, readJson(DRAFTS_KEY, []).filter((draft) => draft.id !== id)); },
  readWorkspace() { ensureSeeded(); return readJson(WORKSPACE_KEY, seedWorkspace()); },
  writeWorkspace(workspace) { ensureSeeded(); writeJson(WORKSPACE_KEY, workspace); },
  exportData() {
    ensureSeeded();
    return { version: EXPORT_VERSION, kind: "lacw-local-test-data", exported_at: timestamp(), warning: "Fictional development data only. Do not use for real client information.", drafts: readJson(DRAFTS_KEY, []), workspace: readJson(WORKSPACE_KEY, seedWorkspace()) };
  },
  importData(payload) { validateImport(payload); writeJson(DRAFTS_KEY, payload.drafts); writeJson(WORKSPACE_KEY, payload.workspace); },
  reset() { writeJson(DRAFTS_KEY, seedDrafts()); writeJson(WORKSPACE_KEY, seedWorkspace()); },
};
